import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Globe, Filter, Loader2, Users, Building2, MapPin, ChevronRight,
  BarChart3, AlertTriangle, CheckCircle2, HelpCircle, Clock,
  Scale, RefreshCw, Info, ChevronDown, ChevronUp, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBRL = (val) => {
  if (val == null) return 'N/A';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};

const COLORS = ['#1E3A5F', '#2E7D32', '#C62828', '#F57F17', '#6A1B9A', '#00838F', '#E65100', '#1565C0', '#4E342E', '#37474F'];

const SituacaoBadge = ({ situacao }) => {
  if (!situacao) return null;
  const lower = situacao.toLowerCase();
  let color = 'bg-[#F4F6F8] text-[#475569]';
  if (lower.includes('deferido') || lower.includes('eleito')) color = 'bg-[#E8F5E9] text-[#2E7D32]';
  else if (lower.includes('indeferido') || lower.includes('cassado')) color = 'bg-[#FFEBEE] text-[#C62828]';
  else if (lower.includes('aguardando') || lower.includes('análise')) color = 'bg-[#FFF8E1] text-[#F57F17]';
  return <Badge className={`text-[10px] ${color}`}>{situacao}</Badge>;
};

const GravidadeBadge = ({ gravidade }) => {
  if (!gravidade) return null;
  const map = {
    alta: { icon: AlertTriangle, color: 'text-[#C62828] bg-[#FFEBEE]', label: 'Alta' },
    média: { icon: HelpCircle, color: 'text-[#F57F17] bg-[#FFF8E1]', label: 'Média' },
    baixa: { icon: CheckCircle2, color: 'text-[#2E7D32] bg-[#E8F5E9]', label: 'Baixa' },
  };
  const cfg = map[gravidade.toLowerCase()] || map.baixa;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const map = {
    'em andamento': { icon: Clock, color: 'text-[#F57F17] bg-[#FFF8E1]' },
    'arquivado': { icon: CheckCircle2, color: 'text-[#94A3B8] bg-[#F4F6F8]' },
    'julgado': { icon: Scale, color: 'text-[#1E3A5F] bg-[#E3F2FD]' },
    'condenação confirmada': { icon: AlertTriangle, color: 'text-[#C62828] bg-[#FFEBEE]' },
    'absolvição': { icon: CheckCircle2, color: 'text-[#2E7D32] bg-[#E8F5E9]' },
    'não confirmado': { icon: HelpCircle, color: 'text-[#94A3B8] bg-[#F4F6F8]' },
  };
  const cfg = map[status.toLowerCase()] || map['não confirmado'];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const StatsChart = ({ data, title, dataKey, nameKey, icon: Icon }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.slice(0, 10);
  return (
    <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid={`stats-chart-${dataKey}`}>
      <h4 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#1E3A5F]" />} {title}
      </h4>
      <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 28)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 5, right: 15 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} />
          <YAxis dataKey={nameKey} type="category" width={100} tick={{ fontSize: 10, fill: '#475569' }} />
          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 11 }} />
          <Bar dataKey="total" radius={[0, 4, 4, 0]} barSize={18}>
            {chartData.map((_, idx) => (
              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const ProcessSummaryPanel = ({ processes, candidateName }) => {
  const [summaries, setSummaries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disclaimer, setDisclaimer] = useState('');

  const fetchSummary = async () => {
    if (!processes || processes.length === 0) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/electoral/process-summary`, {
        processes: processes.slice(0, 10),
        candidate_name: candidateName,
      }, { timeout: 70000 });
      setSummaries(resp.data.summaries || []);
      setDisclaimer(resp.data.disclaimer || '');
    } catch {
      toast.error('Erro ao gerar resumo dos processos.');
    } finally {
      setLoading(false);
    }
  };

  if (!processes || processes.length === 0) return null;

  return (
    <div className="mt-4" data-testid="process-summary-panel">
      {!summaries && (
        <Button size="sm" onClick={fetchSummary} disabled={loading} className="bg-[#1E3A5F] hover:bg-[#152B47] text-white text-xs" data-testid="summarize-processes-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analisando processos...</> : <><Scale className="w-4 h-4 mr-1" /> Resumir processos com IA</>}
        </Button>
      )}
      {summaries && (
        <div className="space-y-3 mt-3">
          {summaries.map((s, idx) => (
            <div key={idx} className="p-3 border border-[#E2E8F0] rounded-md bg-[#FAFBFC]" data-testid={`process-summary-${idx}`}>
              <div className="flex flex-wrap gap-2 mb-2">
                <StatusBadge status={s.status} />
                <GravidadeBadge gravidade={s.gravidade} />
                <Badge variant="outline" className="text-[10px] border-[#94A3B8] text-[#475569]">{s.classificacao}</Badge>
              </div>
              <p className="text-sm text-[#475569] mb-1">{s.resumo}</p>
              <p className="text-[10px] text-[#94A3B8] italic">{s.processo}</p>
            </div>
          ))}
          {disclaimer && (
            <div className="flex items-start gap-2 p-3 bg-[#FFF8E1] border border-[#F57F17]/20 rounded-md" data-testid="process-disclaimer">
              <Info className="w-4 h-4 text-[#F57F17] shrink-0 mt-0.5" />
              <p className="text-xs text-[#F57F17]">{disclaimer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Electoral360 = () => {
  // Filter state
  const [filters, setFilters] = useState(null);
  const [electionType, setElectionType] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [selectedCargo, setSelectedCargo] = useState('');
  const [selectedParty, setSelectedParty] = useState('');
  const [municipalities, setMunicipalities] = useState([]);
  const [loadingMunis, setLoadingMunis] = useState(false);

  // Results state
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [searched, setSearched] = useState(false);

  // Dossier state
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [filtersError, setFiltersError] = useState(false);

  // Load filters on mount — silent fallback if backend is offline
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const resp = await axios.get(`${API}/electoral/filters`, { timeout: 10000 });
        setFilters(resp.data);
        setFiltersError(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Electoral360] filters unavailable:', err?.response?.status || err?.message);
        setFiltersError(true);
      }
    };
    loadFilters();
  }, []);

  const retryFilters = async () => {
    setFiltersError(false);
    try {
      const resp = await axios.get(`${API}/electoral/filters`, { timeout: 10000 });
      setFilters(resp.data);
    } catch { setFiltersError(true); }
  };

  // Available years/cargos based on election type
  const currentType = filters?.election_types?.find(t => t.value === electionType);
  const availableYears = currentType?.years || [];
  const availableCargos = currentType?.cargos || [];

  // Load municipalities when state changes
  useEffect(() => {
    if (!selectedState || !selectedYear) return;
    if (electionType === 'federal') return; // No municipality for federal
    setLoadingMunis(true);
    setSelectedMunicipality('');
    axios.get(`${API}/electoral/municipalities`, {
      params: { state: selectedState, year: parseInt(selectedYear) },
      timeout: 15000,
    }).then(resp => {
      setMunicipalities(resp.data.municipalities || []);
    }).catch(() => {
      setMunicipalities([]);
    }).finally(() => setLoadingMunis(false));
  }, [selectedState, selectedYear, electionType]);

  // Reset downstream filters on type change
  useEffect(() => {
    setSelectedYear('');
    setSelectedState('');
    setSelectedMunicipality('');
    setSelectedCargo('');
    setSelectedParty('');
    setCandidates([]);
    setStats(null);
    setSearched(false);
    setSelectedCandidate(null);
  }, [electionType]);

  useEffect(() => {
    setSelectedMunicipality('');
    setSelectedCargo('');
    setSelectedParty('');
  }, [selectedState]);

  const handleSearch = useCallback(async () => {
    if (!selectedYear) {
      toast.error('Selecione o ano da eleição.');
      return;
    }
    setLoadingSearch(true);
    setLoadingStats(true);
    setSearched(true);
    setSelectedCandidate(null);

    const params = {
      year: parseInt(selectedYear),
      state: selectedState,
      municipality: selectedMunicipality,
      cargo: selectedCargo ? parseInt(selectedCargo) : 0,
      party: selectedParty,
      per_page: 50,
    };

    try {
      const [searchResp, statsResp] = await Promise.all([
        axios.get(`${API}/electoral/search`, { params, timeout: 30000 }),
        axios.get(`${API}/electoral/stats`, { params: { year: params.year, state: params.state, municipality: params.municipality, cargo: params.cargo }, timeout: 30000 }),
      ]);
      setCandidates(searchResp.data.candidates || []);
      setTotalCandidates(searchResp.data.total || 0);
      setStats(statsResp.data);
    } catch {
      toast.error('Erro ao buscar dados eleitorais.');
    } finally {
      setLoadingSearch(false);
      setLoadingStats(false);
    }
  }, [selectedYear, selectedState, selectedMunicipality, selectedCargo, selectedParty]);

  const handleSelectCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    setLoadingDossier(true);
    try {
      const resp = await axios.get(`${API}/tse/search`, {
        params: { name: candidate.nome_completo || candidate.nome_urna, year: parseInt(selectedYear), state: selectedState || 'BR' },
        timeout: 25000,
      });
      const tseMatch = (resp.data.candidates || []).find(c => c.id === candidate.id) || (resp.data.candidates || [])[0];
      setDossier(tseMatch || null);
    } catch {
      setDossier(null);
    } finally {
      setLoadingDossier(false);
    }
  };

  // Derive unique parties from stats for filter
  const partyOptions = stats?.by_party?.map(p => p.partido) || [];

  return (
    <div className="space-y-6" data-testid="electoral-360-page">
      {/* Header */}
      <div className="text-center py-4 md:py-8">
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2" data-testid="electoral-title">
          Monitoramento Eleitoral 360°
        </h1>
        <p className="text-sm sm:text-base text-[#475569] max-w-2xl mx-auto px-2">
          Auditoria cidadã com dados oficiais do TSE. Filtre por eleição, estado, município, cargo e partido.
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="electoral-filters">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-[#1E3A5F]" />
          <h3 className="text-base font-bold text-[#0F1C2E]">Filtro Hierárquico</h3>
        </div>

        {filtersError && (
          <div
            className="mb-3 p-3 bg-[#FFF8E1] border border-[#F0D000]/40 rounded-md flex items-start justify-between gap-3"
            data-testid="filters-fallback"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#F57F17] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-[#0F1C2E]">Dados temporariamente indisponíveis</p>
                <p className="text-[11px] text-[#475569]">
                  Não foi possível carregar os filtros eleitorais agora. Tente novamente em instantes.
                </p>
              </div>
            </div>
            <button
              onClick={retryFilters}
              className="text-[11px] font-bold text-[#006030] hover:underline shrink-0"
              data-testid="filters-retry"
            >
              Tentar novamente
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Election Type */}
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Tipo</label>
            <Select value={electionType} onValueChange={setElectionType}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                {(filters?.election_types || []).map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year */}
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Ano</label>
            <Select value={selectedYear} onValueChange={setSelectedYear} disabled={!electionType}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="filter-year">
                <SelectValue placeholder="Ano" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* State */}
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Estado</label>
            <Select value={selectedState} onValueChange={setSelectedState} disabled={!selectedYear}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="filter-state">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {(filters?.states || []).map(s => (
                  <SelectItem key={s.uf} value={s.uf}>{s.uf} · {s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Municipality */}
          {electionType === 'municipal' && (
            <div>
              <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Município</label>
              <Select value={selectedMunicipality} onValueChange={setSelectedMunicipality} disabled={!selectedState || loadingMunis}>
                <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="filter-municipality">
                  <SelectValue placeholder={loadingMunis ? 'Carregando...' : 'Município'} />
                </SelectTrigger>
                <SelectContent>
                  {municipalities.map(m => (
                    <SelectItem key={m.code} value={m.code}>{m.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Cargo */}
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Cargo</label>
            <Select value={selectedCargo || "all"} onValueChange={(v) => setSelectedCargo(v === "all" ? "" : v)} disabled={!electionType}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="filter-cargo">
                <SelectValue placeholder="Cargo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {availableCargos.map(c => (
                  <SelectItem key={c.code} value={String(c.code)}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Party (populated after first search) */}
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Partido</label>
            <Select value={selectedParty || "all"} onValueChange={(v) => setSelectedParty(v === "all" ? "" : v)} disabled={partyOptions.length === 0 && !selectedParty}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="filter-party">
                <SelectValue placeholder="Partido" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {partyOptions.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          onClick={handleSearch}
          disabled={!selectedYear || loadingSearch}
          className="mt-4 bg-[#1E3A5F] hover:bg-[#152B47] text-white font-medium h-10 px-6"
          data-testid="electoral-search-btn"
        >
          {loadingSearch ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Consultando TSE...</> : <><Globe className="w-4 h-4 mr-2" /> Consultar</>}
        </Button>
      </Card>

      {/* Stats Dashboard */}
      {stats && (
        <div data-testid="electoral-stats-section">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 mb-3 text-sm font-bold text-[#0F1C2E] hover:text-[#1E3A5F] transition-colors"
            data-testid="toggle-stats-btn"
          >
            <BarChart3 className="w-4 h-4" />
            Estatísticas: {stats.total_candidatos} candidato(s)
            {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Summary cards */}
              <Card className="p-4 bg-[#1E3A5F] text-white rounded-md" data-testid="stats-total-card">
                <p className="text-xs font-medium opacity-80">Total de Candidatos</p>
                <p className="text-3xl font-black mt-1">{stats.total_candidatos}</p>
                <p className="text-xs opacity-60 mt-1">
                  {stats.filters?.year} | {stats.filters?.state || 'Nacional'}
                </p>
              </Card>
              <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="stats-parties-card">
                <p className="text-xs font-bold text-[#94A3B8] uppercase">Partidos</p>
                <p className="text-3xl font-black text-[#0F1C2E] mt-1">{stats.by_party?.length || 0}</p>
                <p className="text-xs text-[#94A3B8] mt-1">legenda(s) com candidatura(s)</p>
              </Card>
              <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="stats-cargos-card">
                <p className="text-xs font-bold text-[#94A3B8] uppercase">Cargos Disputados</p>
                <p className="text-3xl font-black text-[#0F1C2E] mt-1">{stats.by_cargo?.length || 0}</p>
                <p className="text-xs text-[#94A3B8] mt-1">cargo(s) em disputa</p>
              </Card>

              {/* Charts */}
              <StatsChart data={stats.by_party} title="Candidatos por Partido" dataKey="total" nameKey="partido" icon={Users} />
              <StatsChart data={stats.by_cargo} title="Candidatos por Cargo" dataKey="total" nameKey="cargo" icon={Building2} />
              <StatsChart data={stats.by_situacao} title="Situação das Candidaturas" dataKey="total" nameKey="situacao" icon={CheckCircle2} />
            </div>
          )}

          <p className="text-[10px] text-[#94A3B8] mt-2">
            Dados atualizados em: {new Date(stats.updated_at).toLocaleString('pt-BR')} · Fonte: TSE DivulgaCandContas
          </p>
        </div>
      )}

      {/* Results */}
      {searched && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="electoral-results">
          {/* Candidate List */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
              <h3 className="text-base font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#1E3A5F]" />
                Candidatos ({totalCandidates})
              </h3>

              {loadingSearch && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin text-[#94A3B8]" />
                  <span className="text-sm text-[#94A3B8]">Consultando TSE...</span>
                </div>
              )}

              {!loadingSearch && candidates.length === 0 && (
                <div className="text-center py-8" data-testid="no-candidates">
                  <MapPin className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
                  <p className="text-sm text-[#94A3B8]">Nenhum candidato encontrado para os filtros selecionados.</p>
                </div>
              )}

              {!loadingSearch && candidates.length > 0 && (
                <div className="space-y-2 max-h-[600px] overflow-y-auto custom-scrollbar">
                  {candidates.map((c, idx) => (
                    <div
                      key={c.id || idx}
                      onClick={() => handleSelectCandidate(c)}
                      data-testid={`electoral-candidate-${idx}`}
                      className={`p-3 border rounded-md cursor-pointer transition-all duration-200 hover:border-[#1E3A5F] hover:shadow-sm ${
                        selectedCandidate?.id === c.id ? 'border-[#1E3A5F] bg-[#F4F6F8]' : 'border-[#E2E8F0]'
                      }`}
                    >
                      <p className="font-semibold text-[#0F1C2E] text-sm truncate">{c.nome_urna || c.nome_completo}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.partido && <Badge variant="outline" className="text-[10px] border-[#1E3A5F] text-[#1E3A5F]">{c.partido}</Badge>}
                        {c.cargo && <Badge variant="outline" className="text-[10px] border-[#94A3B8] text-[#475569]">{c.cargo}</Badge>}
                        {c.numero && <Badge variant="outline" className="text-[10px] border-[#94A3B8] text-[#475569]">N.{c.numero}</Badge>}
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <SituacaoBadge situacao={c.situacao} />
                        {c.total_bens != null && <span className="text-[10px] text-[#2E7D32] font-medium">{formatBRL(c.total_bens)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Dossier */}
          <div className="lg:col-span-2">
            {!selectedCandidate && (
              <Card className="p-8 md:p-12 bg-white border border-[#E2E8F0] rounded-md text-center" data-testid="empty-dossier">
                <div className="max-w-sm mx-auto">
                  <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center mx-auto mb-5">
                    <Globe className="w-8 h-8 text-[#94A3B8]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F1C2E] mb-2">Selecione um Candidato</h3>
                  <p className="text-sm text-[#475569]">Clique em um candidato da lista para visualizar o dossiê completo.</p>
                </div>
              </Card>
            )}

            {selectedCandidate && (
              <Card className="p-4 sm:p-6 bg-white border border-[#E2E8F0] rounded-md" data-testid="electoral-dossier">
                {/* Dossier Header */}
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-14 h-14 rounded-full bg-[#F4F6F8] flex items-center justify-center shrink-0">
                    <Users className="w-7 h-7 text-[#1E3A5F]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-black text-[#0F1C2E] tracking-tight" data-testid="dossier-name">
                      {selectedCandidate.nome_completo || selectedCandidate.nome_urna}
                    </h2>
                    <p className="text-sm text-[#475569] mt-0.5">
                      {selectedCandidate.nome_urna && selectedCandidate.nome_completo && selectedCandidate.nome_urna !== selectedCandidate.nome_completo && (
                        <>Nome de urna: {selectedCandidate.nome_urna} · </>
                      )}
                      {selectedCandidate.cargo}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCandidate.partido && <Badge className="bg-[#1E3A5F] text-white text-xs">{selectedCandidate.partido}</Badge>}
                      {selectedCandidate.numero && <Badge variant="outline" className="text-xs border-[#94A3B8] text-[#475569]">N. {selectedCandidate.numero}</Badge>}
                      <SituacaoBadge situacao={selectedCandidate.situacao} />
                      {selectedCandidate.reeleicao && <Badge className="bg-[#FFF8E1] text-[#F57F17] text-xs">Reeleição</Badge>}
                    </div>
                  </div>
                </div>

                {loadingDossier && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin text-[#94A3B8]" />
                    <span className="text-sm text-[#94A3B8]">Carregando dossiê...</span>
                  </div>
                )}

                {!loadingDossier && dossier && (
                  <div className="space-y-5">
                    {/* Dados Cadastrais */}
                    <div>
                      <h4 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2 pb-2 border-b border-[#E2E8F0]">
                        <Building2 className="w-4 h-4 text-[#1E3A5F]" /> Dados Cadastrais
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-[#94A3B8] text-xs">Nome Completo</span><p className="text-[#0F1C2E] font-medium">{dossier.nome_completo || 'N/A'}</p></div>
                        <div><span className="text-[#94A3B8] text-xs">Nome de Urna</span><p className="text-[#0F1C2E] font-medium">{dossier.nome_urna || 'N/A'}</p></div>
                        <div><span className="text-[#94A3B8] text-xs">Partido</span><p className="text-[#0F1C2E] font-medium">{dossier.partido || selectedCandidate.partido || 'N/A'}</p></div>
                        <div><span className="text-[#94A3B8] text-xs">Cargo</span><p className="text-[#0F1C2E] font-medium">{dossier.cargo || selectedCandidate.cargo || 'N/A'}</p></div>
                        <div><span className="text-[#94A3B8] text-xs">Número</span><p className="text-[#0F1C2E] font-medium">{dossier.numero || selectedCandidate.numero || 'N/A'}</p></div>
                        <div><span className="text-[#94A3B8] text-xs">Situação</span><SituacaoBadge situacao={dossier.situacao || selectedCandidate.situacao} /></div>
                      </div>
                    </div>

                    {/* Patrimônio */}
                    {dossier.total_bens != null && (
                      <div>
                        <h4 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2 pb-2 border-b border-[#E2E8F0]">
                          <Scale className="w-4 h-4 text-[#2E7D32]" /> Patrimônio Declarado
                        </h4>
                        <p className="text-2xl font-black text-[#2E7D32]" data-testid="dossier-bens-total">{formatBRL(dossier.total_bens)}</p>
                        {dossier.bens && dossier.bens.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            {dossier.bens.slice(0, 10).map((b, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs p-2 bg-[#FAFBFC] rounded border border-[#E2E8F0]">
                                <span className="text-[#475569] truncate flex-1">{b.descricao || b.tipo}</span>
                                <span className="text-[#0F1C2E] font-medium ml-2 shrink-0">{formatBRL(b.valor)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Ficha Limpa */}
                    {dossier.ficha_limpa_motivo && (
                      <div className="p-3 bg-[#FFEBEE] border border-[#C62828]/20 rounded-md" data-testid="dossier-ficha-limpa">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-[#C62828]" />
                          <span className="text-sm font-bold text-[#C62828]">Alerta de Ficha Limpa</span>
                        </div>
                        <p className="text-xs text-[#C62828]">Motivo identificado no registro do TSE.</p>
                      </div>
                    )}

                    {/* Eleições Anteriores */}
                    {dossier.eleicoes_anteriores && dossier.eleicoes_anteriores.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2 pb-2 border-b border-[#E2E8F0]">
                          <RefreshCw className="w-4 h-4 text-[#F57F17]" /> Histórico Eleitoral
                        </h4>
                        <div className="space-y-2">
                          {dossier.eleicoes_anteriores.map((e, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs p-2.5 bg-[#FAFBFC] rounded border border-[#E2E8F0]">
                              <div>
                                <span className="font-bold text-[#0F1C2E]">{e.ano}</span>
                                <span className="text-[#475569] ml-2">{e.cargo} ({e.partido})</span>
                                {e.local && <span className="text-[#94A3B8] ml-1">· {e.local}</span>}
                              </div>
                              <Badge className={`text-[10px] ${
                                (e.resultado || '').toLowerCase().includes('eleito') ? 'bg-[#E8F5E9] text-[#2E7D32]'
                                : (e.resultado || '').toLowerCase().includes('não') ? 'bg-[#FFEBEE] text-[#C62828]'
                                : 'bg-[#F4F6F8] text-[#475569]'
                              }`}>
                                {e.resultado || 'N/A'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Análise IA — botão para análise completa */}
                    <div className="pt-3 border-t border-[#E2E8F0]">
                      <Button
                        size="sm"
                        onClick={() => {
                          window.open(`/?analyze=${encodeURIComponent(dossier.nome_completo || selectedCandidate.nome_completo || selectedCandidate.nome_urna)}`, '_self');
                        }}
                        className="bg-[#1E3A5F] hover:bg-[#152B47] text-white text-xs"
                        data-testid="full-analysis-btn"
                      >
                        <Scale className="w-4 h-4 mr-1" /> Análise Completa de Integridade (IA)
                      </Button>
                    </div>
                  </div>
                )}

                {!loadingDossier && !dossier && (
                  <div className="text-center py-6">
                    <p className="text-sm text-[#94A3B8]">Dados detalhados não encontrados no TSE para este candidato.</p>
                  </div>
                )}

                {/* LGPD Notice */}
                <div className="mt-5 p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md" data-testid="lgpd-notice">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#94A3B8] shrink-0 mt-0.5" />
                    <p className="text-[10px] text-[#94A3B8] leading-relaxed">
                      Dados obtidos exclusivamente de fontes públicas oficiais (TSE DivulgaCandContas), em conformidade com a LGPD e o princípio do interesse público eleitoral.
                      Informações apresentadas refletem registros oficiais e não constituem juízo de valor.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Electoral360;
